<template>
  <div class="pt-3 container-fluid">
    <div class="row">

      <!-- ── LEFT PANEL ── -->
      <div class="pl-0 col-5">
        <div class="card-wrapper">

          <!-- Branch selectors -->
          <div class="mb-2 card">
            <div class="card-header">
              <h5 class="mb-0">Transfer Details</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label class="form-control-label">From Branch</label>
                <select v-model="form.fromBranchId" @change="onFromBranchChange" class="form-control">
                  <option value="">Select branch...</option>
                  <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <small v-if="errors.fromBranchId" class="text-danger">{{ errors.fromBranchId }}</small>
              </div>

              <div class="mb-3">
                <label class="form-control-label">To Branch</label>
                <select v-model="form.toBranchId" class="form-control">
                  <option value="">Select branch...</option>
                  <option v-for="b in toBranches" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
                <small v-if="errors.toBranchId" class="text-danger">{{ errors.toBranchId }}</small>
              </div>

              <div class="mb-3">
                <label class="form-control-label">Notes (optional)</label>
                <textarea v-model="form.notes" rows="2" placeholder="Optional notes" class="form-control"></textarea>
              </div>
            </div>
          </div>

          <!-- Cart header -->
          <div style="display:grid;grid-template-columns:2fr 80px 70px 80px 28px;gap:6px;padding:8px 12px;background:#f8f9fa;border:1px solid #e9ecef;border-radius:6px 6px 0 0;">
            <span style="font-size:11px;color:#6c757d;font-weight:600;text-transform:uppercase">Product</span>
            <span style="font-size:11px;color:#6c757d;font-weight:600;text-transform:uppercase;text-align:center">Price (₹)</span>
            <span style="font-size:11px;color:#6c757d;font-weight:600;text-transform:uppercase;text-align:center">Qty</span>
            <span style="font-size:11px;color:#6c757d;font-weight:600;text-transform:uppercase;text-align:right">Total</span>
            <span></span>
          </div>

          <!-- Cart empty -->
          <div v-if="form.items.length === 0"
            style="text-align:center;padding:32px 16px;color:#adb5bd;border:1px solid #e9ecef;border-top:none;border-radius:0 0 6px 6px;">
            Search and add products from the right panel
          </div>

          <!-- Cart rows -->
          <div style="border:1px solid #e9ecef;border-top:none;border-radius:0 0 6px 6px;max-height:360px;overflow-y:auto;">
            <div v-for="(row, idx) in form.items" :key="row.product_id"
              style="display:grid;grid-template-columns:2fr 80px 70px 80px 28px;gap:6px;padding:8px 12px;border-bottom:1px solid #f1f3f5;align-items:center;">
              <div>
                <div style="font-size:13px;font-weight:500;color:#212529;line-height:1.3">{{ row.name }}</div>
                <div style="font-size:11px;color:#adb5bd">Stock: {{ row.branch_stock }}</div>
              </div>
              <div>
                <input v-model.number="row.unit_price" type="number" min="0" step="0.01"
                  @input="recalc(row)"
                  style="width:100%;border:1px solid #dee2e6;border-radius:4px;padding:3px 6px;font-size:13px;text-align:center" />
              </div>
              <div>
                <input v-model.number="row.quantity" type="number" min="1" :max="row.branch_stock"
                  @input="recalc(row)"
                  style="width:100%;border:1px solid #dee2e6;border-radius:4px;padding:3px 6px;font-size:13px;text-align:center" />
              </div>
              <div style="font-size:13px;font-weight:600;color:#212529;text-align:right">
                ₹ {{ row.total.toFixed(2) }}
              </div>
              <div style="text-align:center">
                <span @click="removeItem(idx)"
                  style="cursor:pointer;color:#dc3545;font-size:20px;line-height:1;font-weight:300">&times;</span>
              </div>
            </div>
          </div>

          <!-- Total + submit -->
          <div v-if="form.items.length" class="mt-3 card">
            <div class="card-body py-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <span style="font-size:15px;font-weight:600;color:#6c757d">Total Amount</span>
                <span style="font-size:20px;font-weight:700;color:#212529">₹ {{ totalAmount.toFixed(2) }}</span>
              </div>
              <small v-if="errors.items" class="text-danger d-block mb-2">{{ errors.items }}</small>
              <base-button type="primary" :disabled="submitting" @click="submit" style="width:100%">
                <span v-if="submitting">Creating Transfer...</span>
                <span v-else>Create Transfer</span>
              </base-button>
            </div>
          </div>

        </div>
      </div>

      <!-- ── RIGHT PANEL: product search ── -->
      <div class="col-7">
        <div class="card" style="min-height:400px">
          <div class="card-header">
            <h5 class="mb-0">Add Products</h5>
          </div>
          <div class="card-body">

            <div class="mb-3" style="position:relative">
              <label class="form-control-label">Search Product / Barcode</label>
              <input
                v-model="searchKeyword"
                @input="onSearchInput"
                @blur="hideResultsDelayed"
                @focus="onSearchFocus"
                type="text"
                class="form-control"
                placeholder="Type product name or barcode..."
                autocomplete="off"
              />
              <small v-if="!form.fromBranchId" class="text-muted">
                Select a From Branch to see branch stock levels.
              </small>

              <!-- Spinner -->
              <div v-if="searching" style="position:absolute;right:12px;top:38px">
                <i class="fas fa-spinner fa-spin" style="color:#adb5bd"></i>
              </div>

              <!-- Results dropdown -->
              <div v-if="searchResults.length && showResults"
                style="position:absolute;left:0;right:0;z-index:100;background:#fff;border:1px solid #dee2e6;border-top:none;border-radius:0 0 6px 6px;max-height:320px;overflow-y:auto;box-shadow:0 4px 12px rgba(0,0,0,0.1)">
                <div v-for="p in searchResults" :key="p.product_id"
                  @mousedown="addProduct(p)"
                  style="padding:10px 14px;cursor:pointer;border-bottom:1px solid #f1f3f5;display:flex;justify-content:space-between;align-items:center"
                  onmouseover="this.style.background='#f0f4ff'" onmouseout="this.style.background='#fff'">
                  <div>
                    <div style="font-size:14px;font-weight:500;color:#212529">{{ p.name }}</div>
                    <div style="font-size:12px;color:#adb5bd">
                      Branch stock: {{ p.branch_stock }}
                    </div>
                  </div>
                  <div style="text-align:right;margin-left:16px;flex-shrink:0">
                    <div style="font-size:14px;font-weight:600;color:#5e72e4">₹ {{ branchRateLabel(p) }}</div>
                    <div style="font-size:11px;color:#adb5bd">branch rate</div>
                  </div>
                </div>
              </div>

              <!-- No results -->
              <div v-if="searchDone && searchResults.length === 0 && showResults"
                style="position:absolute;left:0;right:0;z-index:100;background:#fff;border:1px solid #dee2e6;border-top:none;border-radius:0 0 6px 6px;padding:16px;text-align:center;color:#adb5bd">
                No products found
              </div>
            </div>

            <p style="font-size:12px;color:#adb5bd;margin-top:4px">
              Price is auto-filled from Branch Rate price type. You can edit it in the cart.
            </p>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

var BASE = endpoints.LARAVEL_BASE_URL;
var searchTimer = null;

export default {
  name: "BranchTransferCreate",
  data() {
    return {
      branches: [],
      searchKeyword: "",
      searchResults: [],
      showResults: false,
      searching: false,
      searchDone: false,
      submitting: false,
      errors: {},
      form: {
        fromBranchId: "",
        toBranchId: "",
        notes: "",
        items: [],
      },
    };
  },
  computed: {
    toBranches() {
      var self = this;
      return this.branches.filter(function(b) { return b.id !== self.form.fromBranchId; });
    },
    totalAmount() {
      return this.form.items.reduce(function(s, i) { return s + (i.total || 0); }, 0);
    },
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      try {
        var res = await new Api().call("get", BASE + "/api/v1/branches");
        this.branches = res.data.data || res.data;
      } catch (e) {
        console.error(e);
      }
    },

    onFromBranchChange() {
      this.form.items = [];
      this.searchKeyword = "";
      this.searchResults = [];
      if (this.form.fromBranchId === this.form.toBranchId) {
        this.form.toBranchId = "";
      }
    },

    onSearchFocus() {
      if (this.searchResults.length) this.showResults = true;
    },

    hideResultsDelayed() {
      // Delay so mousedown on a result fires before blur hides the list
      var self = this;
      setTimeout(function() { self.showResults = false; }, 200);
    },

    onSearchInput() {
      clearTimeout(searchTimer);
      this.searchDone = false;
      if (!this.searchKeyword || this.searchKeyword.trim().length < 1) {
        this.searchResults = [];
        this.showResults = false;
        return;
      }
      this.showResults = true;
      var self = this;
      searchTimer = setTimeout(function() { self.fetchProducts(); }, 300);
    },

    async fetchProducts() {
      this.searching = true;
      try {
        var params = new URLSearchParams({ search: this.searchKeyword });
        if (this.form.fromBranchId) params.set("branch_id", this.form.fromBranchId);
        var res = await new Api().call("get", BASE + "/api/v1/products/list?" + params.toString());
        var added = {};
        this.form.items.forEach(function(i) { added[i.product_id] = true; });
        this.searchResults = (res.data.products || []).filter(function(p) { return !added[p.product_id]; });
        this.searchDone = true;
      } catch (e) {
        this.searchResults = [];
      } finally {
        this.searching = false;
      }
    },

    findBranchRate(prices) {
      if (!prices || !prices.length) return null;
      for (var i = 0; i < prices.length; i++) {
        if (prices[i].label && prices[i].label.toLowerCase().indexOf("branch") !== -1) {
          return prices[i];
        }
      }
      return prices[0];
    },

    branchRateLabel(product) {
      var p = this.findBranchRate(product.prices);
      return p ? Number(p.price).toFixed(2) : "—";
    },

    addProduct(product) {
      var priceEntry = this.findBranchRate(product.prices);
      var unitPrice = priceEntry ? Number(priceEntry.price) : 0;
      this.form.items.push({
        product_id:   product.product_id,
        name:         product.name,
        branch_stock: product.branch_stock || 0,
        quantity:     1,
        unit_price:   unitPrice,
        total:        unitPrice,
      });
      this.searchKeyword = "";
      this.searchResults = [];
      this.showResults = false;
    },

    removeItem(idx) {
      this.form.items.splice(idx, 1);
    },

    recalc(item) {
      item.total = parseFloat(((item.quantity || 0) * (item.unit_price || 0)).toFixed(2));
    },

    validate() {
      this.errors = {};
      if (!this.form.fromBranchId) this.errors.fromBranchId = "Select from branch";
      if (!this.form.toBranchId)   this.errors.toBranchId   = "Select to branch";
      if (!this.form.items.length) this.errors.items         = "Add at least one product";
      for (var i = 0; i < this.form.items.length; i++) {
        var item = this.form.items[i];
        if (!item.quantity || item.quantity <= 0)
          this.errors.items = "All quantities must be greater than 0";
        if (item.quantity > item.branch_stock)
          this.errors.items = '"' + item.name + '" exceeds available stock (' + item.branch_stock + ')';
      }
      return Object.keys(this.errors).length === 0;
    },

    async submit() {
      if (!this.validate()) return;
      this.submitting = true;
      try {
        await new Api().call("post", BASE + "/api/v1/branch-transfers", {
          from_branch_id: this.form.fromBranchId,
          to_branch_id:   this.form.toBranchId,
          notes:          this.form.notes || null,
          items: this.form.items.map(function(i) {
            return { product_id: i.product_id, quantity: i.quantity, unit_price: i.unit_price };
          }),
        });
        window.notify({ message: "Transfer created successfully", type: "success", title: "Done" });
        this.$router.push("/branch-transfers");
      } catch (e) {
        var msg = (e && e.response && e.response.data && e.response.data.message) || "Failed to create transfer";
        window.notify({ message: msg, type: "danger", title: "Error" });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
